import { Chevron } from "@components/icons"
import colors from "@constants/colors"
import {
  body14Styles,
  body16MainStyles,
  smallcapsBold14Styles,
  subtextStyles,
} from "@constants/typography"
import {
  FormControl,
  InputLabel,
  NativeSelect,
  NativeSelectProps,
} from "@mui/material"
import React from "react"
import styled, { css } from "styled-components"

const Container = styled.div<{ error?: boolean }>`
  ${({ error }) => css`
    width: 100%;
    padding: 0;
    border: solid 1px ${colors.dividerLineGray};
    border-radius: 2px;
    background-color: transparent;

    .MuiFormControl-root {
      width: 100%;
    }

    .MuiFormLabel-root {
      ${body16MainStyles};
      color: ${colors.mediumGray};
    }

    .MuiFormLabel-filled {
      ${smallcapsBold14Styles}
      color: ${colors.black};
    }

    .MuiNativeSelect-select {
      height: 40px;
      padding: 0 38px 0 14px;
    }

    .MuiNativeSelect-select:focus {
      background-color: transparent;
    }

    .MuiInputBase-root {
      ${body14Styles}
      color: ${error ? colors.errorBackground : colors.mediumDarkGray};
    }

    .MuiInput-underline:before {
      border: none;
    }

    && .MuiInput-underline:hover:before {
      border: none;
    }

    .MuiInput-underline:after {
      border: none;
    }
  `}
`

const Option = styled.option``

const IconWrapper = styled.div`
  position: absolute;
  right: 0;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  display: flex;
  transform: translateY(-1px) rotate(90deg);
  height: 24px;
  width: 24px;
  pointer-events: none;
`

const Error = styled.div`
  ${subtextStyles}
  margin-top: 12px;
  padding-left: 30px;
  color: ${colors.errorMui};
`

const IconComponent = () => (
  <IconWrapper>
    <Chevron strokeColor={colors.mediumGray} />
  </IconWrapper>
)

interface SelectInputProps extends NativeSelectProps {
  error?: boolean
  label?: string
  name: string
  options: Array<{
    label: number | string
    value: number | string
  }>
  variant?: "filled" | "outlined" | "standard"
}

const SelectInput: React.FC<SelectInputProps> = ({
  error,
  label,
  name,
  onBlur,
  onChange,
  options,
  value,
  variant,
}) => {
  return (
    <Container error={error}>
      <FormControl>
        {label && (
          <InputLabel
            htmlFor="uncontrolled-native"
            variant={variant || "standard"}
          >
            {label}
          </InputLabel>
        )}
        <NativeSelect
          IconComponent={IconComponent}
          id={name}
          inputProps={{ name }}
          onBlur={onBlur}
          onChange={onChange}
          value={value}
        >
          <Option aria-label="None" value="" />
          {options.map(({ label, value }) => (
            <Option key={value} value={value}>
              {label}
            </Option>
          ))}
        </NativeSelect>
      </FormControl>
      {error && <Error>{error}</Error>}
    </Container>
  )
}

export const RHFSelectInput = React.forwardRef<
  HTMLSelectElement,
  SelectInputProps
>((props: SelectInputProps, ref) => <SelectInput {...props} inputRef={ref} />)

export default SelectInput
